import Papa from 'papaparse';

/**
 * 水资源配置数据工具函数
 * 处理水资源优化和实际用水数据的解析、对比和可视化
 */

const DATA_BASE_PATH = '/data/waterResource/';

// 产业类型映射
const INDUSTRY_MAP = {
  'LIV': '生活用水',
  'IND': '工业用水', 
  'AGR': '农业用水',
  'ECO': '生态用水'
};

// 城市名称映射
const CITY_MAP = {
  'MEIZHOU': '梅州',
  'HEYUAN': '河源',
  'SHAOGUAN': '韶关',
  'HUIZHOU': '惠州',
  'GUANGZHOU': '广州',
  'DONGGUAN': '东莞',
  'SHENZHEN': '深圳',
  'FOSHAN': '佛山',
  'JIANGMEN': '江门',
  'ZHONGSHAN': '中山',
  'ZHUHAI': '珠海',
  'DONGUAN': '东莞', // 数据中的拼写变体
  'NANSHA': '南沙',
  'ZENGCHENG': '增城'
};

/**
 * 解析文件名获取元数据
 * @param {string} fileName - 文件名，格式如：七城区四产业_2020_模型2_优化用水.csv
 * @returns {Object} - {regions, year, model, dataType}
 */
export const parseWaterResourceFileName = (fileName) => {
  const nameWithoutExt = fileName.replace(/\.[^/.]+$/, '');
  const parts = nameWithoutExt.split('_');
  
  if (parts.length >= 4) {
    return {
      regions: parts[0] || '', // 五城区三产业、七城区四产业、九城区四产业
      year: parseInt(parts[1]) || 2020,
      model: parts[2] || '', // 模型1、模型2、模型3、统计
      dataType: parts[3] || '' // 优化用水、实际用水
    };
  }
  
  return {
    regions: '',
    year: 2020,
    model: '',
    dataType: ''
  };
};

/**
 * 加载水资源CSV数据
 * @param {string} fileName - CSV文件名
 * @returns {Promise<Array>} - 解析后的数据数组
 */
export const loadWaterResourceData = async (fileName) => {
  try {
    const response = await fetch(`${DATA_BASE_PATH}${fileName}`);
    if (!response.ok) {
      throw new Error(`文件 ${fileName} 不存在或无法访问 (状态: ${response.status})`);
    }
    
    const csvText = await response.text();
    return parseWaterResourceCSV(csvText);
  } catch (error) {
    console.error('加载水资源数据失败:', error);
    throw error;
  }
};

/**
 * 解析水资源CSV数据
 * @param {string} csvText - CSV文本内容
 * @returns {Array} - 解析后的数据数组
 */
export const parseWaterResourceCSV = (csvText) => {
  const result = Papa.parse(csvText, {
    header: false,
    skipEmptyLines: true,
    transform: (value) => value.trim()
  });

  if (result.errors.length > 0) {
    console.warn('CSV解析警告:', result.errors);
  }

  // 解析数据格式："X( MEIZHOU, LIV)",2.86 或 "AVGDAT( MEIZHOU, LIV)",2.6
  const data = result.data.map((row, index) => {
    if (row.length >= 2) {
      const identifier = row[0] || '';
      const value = parseFloat(row[1]) || 0;
      
      // 解析标识符获取城市和产业信息
      const match = identifier.match(/\(\s*([^,]+),\s*([^)]+)\s*\)/);
      if (match) {
        const city = match[1].trim();
        const industry = match[2].trim();
        
        return {
          city: city,
          cityName: CITY_MAP[city] || city,
          industry: industry,
          industryName: INDUSTRY_MAP[industry] || industry,
          value: value,
          identifier: identifier
        };
      }
    }
    return null;
  }).filter(item => item !== null);

  return data;
};

/**
 * 获取可用的数据配置
 * @returns {Object} - 包含可用年份、区域等配置信息
 */
export const getAvailableConfigurations = () => {
  return {
    years: [2020, 2023, 2024],
    regions: [
      { key: '五城区三产业', label: '五城区三产业', years: [2020, 2023] },
      { key: '七城区四产业', label: '七城区四产业', years: [2020, 2024] },
      { key: '九城区四产业', label: '九城区四产业', years: [2020, 2024] }
    ],
    industries: [
      { key: 'LIV', label: '生活用水' },
      { key: 'IND', label: '工业用水' },
      { key: 'AGR', label: '农业用水' },
      { key: 'ECO', label: '生态用水' }
    ]
  };
};

/**
 * 获取指定区域可用的年份
 * @param {string} region - 区域名称
 * @returns {Array} - 可用年份数组
 */
export const getAvailableYearsForRegion = (region) => {
  const configurations = getAvailableConfigurations();
  const regionConfig = configurations.regions.find(r => r.key === region);
  return regionConfig ? regionConfig.years : [2020];
};

/**
 * 根据年份和区域获取对应的文件名
 * @param {number} year - 年份
 * @param {string} region - 区域
 * @returns {Object} - {optimizedFile, actualFile}
 */
export const getDataFileNames = (year, region) => {
  const modelMap = {
    '五城区三产业': '模型1',
    '七城区四产业': '模型2', 
    '九城区四产业': '模型3'
  };
  
  const model = modelMap[region] || '模型1';
  
  return {
    optimizedFile: `${region}_${year}_${model}_优化用水.csv`,
    actualFile: `${region}_${year}_统计_实际用水.csv`
  };
};

/**
 * 检查数据文件是否存在
 * @param {number} year - 年份
 * @param {string} region - 区域
 * @returns {Promise<boolean>} - 文件是否存在
 */
export const checkDataFilesExist = async (year, region) => {
  try {
    const fileNames = getDataFileNames(year, region);
    
    // 检查两个文件是否都存在
    const checkPromises = [fileNames.optimizedFile, fileNames.actualFile].map(async fileName => {
      try {
        const response = await fetch(`${DATA_BASE_PATH}${fileName}`, { method: 'HEAD' });
        return response.ok;
      } catch {
        return false;
      }
    });
    
    const results = await Promise.all(checkPromises);
    return results.every(exists => exists);
  } catch {
    return false;
  }
};

/**
 * 加载并合并优化和实际用水数据
 * @param {number} year - 年份
 * @param {string} region - 区域
 * @returns {Promise<Array>} - 合并后的对比数据
 */
export const loadComparisonData = async (year, region) => {
  try {
    // 先检查文件是否存在
    const filesExist = await checkDataFilesExist(year, region);
    if (!filesExist) {
      throw new Error(`${region} ${year}年的数据文件不存在或不完整`);
    }
    
    const fileNames = getDataFileNames(year, region);
    
    // 并行加载两个文件
    const [optimizedData, actualData] = await Promise.all([
      loadWaterResourceData(fileNames.optimizedFile),
      loadWaterResourceData(fileNames.actualFile)
    ]);

    // 验证数据
    if (!optimizedData || optimizedData.length === 0) {
      throw new Error('优化用水数据为空');
    }
    if (!actualData || actualData.length === 0) {
      throw new Error('实际用水数据为空');
    }

    // 合并数据进行对比
    const comparisonData = [];
    
    // 创建城市-产业组合的映射
    const actualDataMap = new Map();
    actualData.forEach(item => {
      const key = `${item.city}_${item.industry}`;
      actualDataMap.set(key, item);
    });

    // 合并优化数据和实际数据
    optimizedData.forEach(optimizedItem => {
      const key = `${optimizedItem.city}_${optimizedItem.industry}`;
      const actualItem = actualDataMap.get(key);
      
      if (actualItem) {
        comparisonData.push({
          city: optimizedItem.city,
          cityName: optimizedItem.cityName,
          industry: optimizedItem.industry,
          industryName: optimizedItem.industryName,
          optimizedValue: optimizedItem.value,
          actualValue: actualItem.value,
          label: `${optimizedItem.cityName}-${optimizedItem.industryName}`
        });
      }
    });

    if (comparisonData.length === 0) {
      throw new Error('没有找到匹配的对比数据');
    }

    return comparisonData;
  } catch (error) {
    console.error('加载对比数据失败:', error);
    throw error;
  }
};

/**
 * 格式化数据用于图表展示
 * @param {Array} comparisonData - 对比数据
 * @returns {Object} - 图表数据格式
 */
export const formatDataForChart = (comparisonData) => {
  const categories = comparisonData.map(item => item.label);
  const optimizedValues = comparisonData.map(item => item.optimizedValue);
  const actualValues = comparisonData.map(item => item.actualValue);

  return {
    categories,
    series: [
      {
        name: '优化用水',
        data: optimizedValues,
        color: '#1890ff'
      },
      {
        name: '实际用水', 
        data: actualValues,
        color: '#ff7875'
      }
    ]
  };
};

/**
 * 计算对比统计信息
 * @param {Array} comparisonData - 对比数据
 * @returns {Object} - 统计信息
 */
export const calculateComparisonStats = (comparisonData) => {
  if (!comparisonData || comparisonData.length === 0) {
    return {
      totalOptimized: 0,
      totalActual: 0,
      difference: 0,
      efficiencyRate: 0,
      count: 0
    };
  }

  const totalOptimized = comparisonData.reduce((sum, item) => sum + item.optimizedValue, 0);
  const totalActual = comparisonData.reduce((sum, item) => sum + item.actualValue, 0);
  const difference = totalActual - totalOptimized;
  const efficiencyRate = totalActual > 0 ? (totalOptimized / totalActual * 100) : 0;

  return {
    totalOptimized: totalOptimized.toFixed(2),
    totalActual: totalActual.toFixed(2), 
    difference: difference.toFixed(2),
    efficiencyRate: efficiencyRate.toFixed(1),
    count: comparisonData.length
  };
};

/**
 * 导出对比数据
 * @param {Array} comparisonData - 对比数据
 * @param {string} fileName - 文件名
 */
export const exportComparisonData = (comparisonData, fileName = 'water_resource_comparison') => {
  if (!comparisonData || comparisonData.length === 0) {
    console.warn('没有数据可以导出');
    return;
  }

  // 准备CSV数据
  const csvData = [
    ['城市', '产业', '优化用水(亿立方米)', '实际用水(亿立方米)', '差值'], // 表头
    ...comparisonData.map(item => [
      item.cityName,
      item.industryName, 
      item.optimizedValue,
      item.actualValue,
      (item.actualValue - item.optimizedValue).toFixed(2)
    ])
  ];

  // 使用Papa Parse转换为CSV
  const csv = Papa.unparse(csvData);
  
  // 创建并下载文件
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${fileName}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

const waterResourceDataUtils = {
  parseWaterResourceFileName,
  loadWaterResourceData,
  parseWaterResourceCSV,
  getAvailableConfigurations,
  getDataFileNames,
  loadComparisonData,
  formatDataForChart,
  calculateComparisonStats,
  exportComparisonData
};

export default waterResourceDataUtils;